import { AdPlatform, Line, Meta } from '@marketing-tool/shared';
import type dayjs from 'dayjs';
import { useMemo } from 'react';

import { StatusBadge } from '../../components/StatusBadge';
import { Table } from '../../components/Table';
import { ThumbnailPreview } from '../../components/ThumbnailPreview';
import { useAds, useEntrySegmentation, useStatistics } from '../../data/game-report';
import * as remote from '../../data/remote-data';
import { formatDeliveryStatus } from '../../lib/status';
import * as utils from '../../lib/utils';

type RowStatus =
    | { platform: AdPlatform.Line; status: Line.DeliveryStatus }
    | { platform: AdPlatform.Meta; status: Meta.EffectiveStatus };

type Row = RowStatus & {
    adId: string;
    adName: string;
    creative?: {
        mediaUrl?: string;
        mediaType: 'IMAGE' | 'VIDEO' | 'ANIMATION';
    };
    statistics: remote.RemoteData<
        {
            currency: string;
            cost: number;
            impressions: number;
            clicks: number;
            dayZeroEntries: remote.RemoteData<number, string>;
            dayOneEntries: remote.RemoteData<number, string>;
        },
        string
    >;
};

// Format utilities
const format = {
    deliveryStatus: formatDeliveryStatus,
    currency: (amount: number, currency: string): string =>
        new Intl.NumberFormat('en-JP', {
            style: 'currency',
            currency,
        }).format(amount),
    number: (amount: number): string =>
        new Intl.NumberFormat('en-JP', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount),
    percentage: (amount: number): string =>
        new Intl.NumberFormat('en-JP', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount),
};

// Sort utilities
const sort = {
    adName: (a: Row, b: Row) => a.adName.localeCompare(b.adName),
    deliveryStatus: (() => {
        const value = ({ platform, status }: Row) => {
            switch (platform) {
                case AdPlatform.Line:
                    return Object.values(Line.DeliveryStatus).indexOf(status);
                case AdPlatform.Meta:
                    return Object.values(Meta.EffectiveStatus).indexOf(status);
                default:
                    utils.assertNever(platform);
            }
        };
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    cost: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.map(statistics, ({ cost }) => cost),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    impressions: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.map(statistics, ({ impressions }) => impressions),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    clicks: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.map(statistics, ({ clicks }) => clicks),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    cpx: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ clicks, cost }) =>
                    remote.map(statistics, ({ clicks, cost }) => (clicks ? cost / clicks : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    clickThroughRate: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ impressions, clicks }) =>
                    remote.map(statistics, ({ impressions, clicks }) => (impressions ? clicks / impressions : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    costPerClick: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ clicks, cost }) =>
                    remote.map(statistics, ({ clicks, cost }) => (clicks ? cost / clicks : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    costPerMille: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ impressions, cost }) =>
                    remote.map(statistics, ({ impressions, cost }) => (impressions ? (cost / impressions) * 1000 : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    dayZeroEntries: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ dayZeroEntries }) => dayZeroEntries),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    dayZeroConversionRate: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ dayZeroEntries, clicks }) =>
                    remote.map(dayZeroEntries, (dayZeroEntries) => (clicks ? dayZeroEntries / clicks : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    dayZeroCostPerConversion: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ dayZeroEntries, cost }) =>
                    remote.map(dayZeroEntries, (dayZeroEntries) => (dayZeroEntries ? cost / dayZeroEntries : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    dayOneReturningUsers: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ dayOneEntries }) => dayOneEntries),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    dayOneReturningRate: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ dayZeroEntries, dayOneEntries }) =>
                    remote.map2(dayZeroEntries, dayOneEntries, (dayZeroEntries, dayOneEntries) =>
                        dayZeroEntries ? dayOneEntries / dayZeroEntries : 0,
                    ),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
    dayOneCostPerConversion: (() => {
        const value = ({ statistics }: Row) =>
            remote.withDefault(
                remote.flatMap(statistics, ({ dayOneEntries, cost }) =>
                    remote.map(dayOneEntries, (dayOneEntries) => (dayOneEntries ? cost / dayOneEntries : 0)),
                ),
                -1,
            );
        return (a: Row, b: Row) => value(a) - value(b);
    })(),
};

// Main component
export const AdTable = ({
    gameId,
    platform,
    campaignId,
    adGroupId,
    from,
    to,
    cohortId,
}: {
    gameId: string;
    platform: AdPlatform;
    campaignId: string | number;
    adGroupId: string | number;
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
    cohortId?: string;
}) => {
    const ads = useAds(gameId, platform, campaignId, adGroupId);
    const statistics = useStatistics(gameId, platform, adGroupId, from, to);
    const dayZeroEntries = useEntrySegmentation(gameId, from, to, 0, cohortId);
    const dayOneEntries = useEntrySegmentation(gameId, from, to, 1, cohortId);

    const data = useMemo<remote.RemoteData<Row[], string>>(
        () =>
            remote.map(ads.data, (ads) =>
                ads.map((ad) => {
                    // Extract creative media info for Line ads
                    let creative: Row['creative'] = undefined;
                    if (ad.platform === AdPlatform.Line && ad.creative) {
                        const { video, image, animation } = ad.creative;
                        // Check both url and object.sourceUrl for media location
                        const getMediaUrl = (media: typeof video) => media?.url || media?.object?.sourceUrl;

                        const videoUrl = getMediaUrl(video);
                        const imageUrl = getMediaUrl(image);
                        const animationUrl = getMediaUrl(animation);

                        if (videoUrl) {
                            creative = { mediaUrl: videoUrl, mediaType: 'VIDEO' };
                        } else if (imageUrl) {
                            creative = { mediaUrl: imageUrl, mediaType: 'IMAGE' };
                        } else if (animationUrl) {
                            creative = { mediaUrl: animationUrl, mediaType: 'ANIMATION' };
                        }
                    }

                    return {
                        ...(ad.platform === AdPlatform.Line
                            ? {
                                  platform: AdPlatform.Line,
                                  status: ad.deliveryStatus,
                              }
                            : ad.platform === AdPlatform.Meta
                              ? {
                                    platform: AdPlatform.Meta,
                                    status: ad.effectiveStatus,
                                }
                              : utils.assertNever(ad)),
                        adId: ad.id.toString(),
                        adName: ad.name,
                        creative,
                        statistics: remote.flatMap(statistics.data, (statistics) => {
                            const stat = statistics.find((stat) => stat.adId.toString() === ad.id.toString());
                            if (!stat) {
                                return remote.NotAsked;
                            }
                            return remote.Success({
                                currency: stat.currency,
                                cost: stat.cost,
                                impressions: stat.impressions,
                                clicks: stat.clicks,
                                dayZeroEntries: remote.flatMap(dayZeroEntries.data, (entries) => {
                                    const entry = entries.find(
                                        (entry) =>
                                            entry.campaignName === stat.campaignName &&
                                            entry.adGroupName === stat.adGroupName &&
                                            entry.adName === stat.adName,
                                    );
                                    if (!entry) {
                                        return remote.NotAsked;
                                    }
                                    return remote.Success(entry.entries);
                                }),
                                dayOneEntries: remote.flatMap(dayOneEntries.data, (entries) => {
                                    const entry = entries.find(
                                        (entry) =>
                                            entry.campaignName === stat.campaignName &&
                                            entry.adGroupName === stat.adGroupName &&
                                            entry.adName === stat.adName,
                                    );
                                    if (!entry) {
                                        return remote.NotAsked;
                                    }
                                    return remote.Success(entry.entries);
                                }),
                            });
                        }),
                    };
                }),
            ),
        [ads.data, statistics.data, dayZeroEntries.data, dayOneEntries.data],
    );

    return (
        <Table
            storageKey="ads"
            data={remote.withDefault(data, [])}
            loading={remote.isLoading(ads.data) || remote.isLoading(statistics.data)}
            id={(row) => row.adId}
            defaultSortColumn="Status"
            density="xs"
            pagination={{ pageSize: 10 }}
            columns={[
                {
                    name: 'Thumbnail',
                    alt: 'Creative thumbnail',
                    type: 'thumbnail',
                    width: 56,
                    render: (row) =>
                        row.creative?.mediaUrl ? (
                            <ThumbnailPreview
                                url={row.creative.mediaUrl}
                                isVideo={row.creative.mediaType === 'VIDEO'}
                                alt={row.adName}
                            />
                        ) : null,
                },
                {
                    name: 'Ad',
                    alt: 'Ad name',
                    render: (row) => row.adName,
                    sort: sort.adName,
                },
                {
                    name: 'Status',
                    alt: 'Delivery status',
                    type: 'status',
                    width: 120,
                    render: ({ status }) => <StatusBadge status={status} />,
                    sort: sort.deliveryStatus,
                },
                {
                    name: 'Cost',
                    type: 'currency',
                    width: 100,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.map(statistics, ({ cost, currency }) => format.currency(cost, currency)),
                            null,
                        ),
                    sort: sort.cost,
                    defaultDirection: 'desc',
                },
                {
                    name: 'Impressions',
                    type: 'number',
                    width: 100,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.map(statistics, ({ impressions }) => format.number(impressions)),
                            null,
                        ),
                    sort: sort.impressions,
                    defaultDirection: 'desc',
                },
                {
                    name: 'Clicks',
                    type: 'number',
                    width: 80,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.map(statistics, ({ clicks }) => format.number(clicks)),
                            null,
                        ),
                    sort: sort.clicks,
                    defaultDirection: 'desc',
                },
                {
                    name: 'CTR',
                    alt: 'Click-through rate',
                    type: 'percentage',
                    width: 80,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.map(statistics, ({ impressions, clicks }) =>
                                impressions ? format.percentage(clicks / impressions) : null,
                            ),
                            null,
                        ),
                    sort: sort.clickThroughRate,
                    defaultDirection: 'desc',
                },
                {
                    name: 'CPC',
                    alt: 'Cost per click',
                    type: 'currency',
                    width: 90,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.map(statistics, ({ clicks, cost, currency }) =>
                                clicks ? format.currency(cost / clicks, currency) : null,
                            ),
                            null,
                        ),
                    sort: sort.costPerClick,
                    defaultDirection: 'desc',
                },
                {
                    name: 'CPM',
                    alt: 'Cost per mille',
                    type: 'currency',
                    width: 90,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.map(statistics, ({ impressions, cost, currency }) =>
                                impressions ? format.currency((cost / impressions) * 1000, currency) : null,
                            ),
                            null,
                        ),
                    sort: sort.costPerMille,
                    defaultDirection: 'desc',
                },
                {
                    name: 'D0UA',
                    alt: 'Day 0 user acquisition',
                    type: 'number',
                    width: 80,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.flatMap(statistics, ({ dayZeroEntries }) =>
                                remote.map(dayZeroEntries, format.number),
                            ),
                            null,
                        ),
                    sort: sort.dayZeroEntries,
                    defaultDirection: 'desc',
                },
                {
                    name: 'D0CVR',
                    alt: 'Day 0 conversion rate',
                    type: 'percentage',
                    width: 80,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.flatMap(statistics, ({ dayZeroEntries, clicks }) =>
                                remote.map(dayZeroEntries, (dayZeroEntries) =>
                                    clicks ? format.percentage(dayZeroEntries / clicks) : null,
                                ),
                            ),
                            null,
                        ),
                    sort: sort.dayZeroConversionRate,
                    defaultDirection: 'desc',
                },
                {
                    name: 'D0CPX',
                    alt: 'Day 0 cost per conversion',
                    type: 'currency',
                    width: 90,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.flatMap(statistics, ({ dayZeroEntries, cost, currency }) =>
                                remote.map(dayZeroEntries, (dayZeroEntries) =>
                                    dayZeroEntries ? format.currency(cost / dayZeroEntries, currency) : null,
                                ),
                            ),
                            null,
                        ),
                    sort: sort.dayZeroCostPerConversion,
                    defaultDirection: 'desc',
                },
                {
                    name: 'D1RU',
                    alt: 'Day 1 returning users',
                    type: 'number',
                    width: 80,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.flatMap(statistics, ({ dayOneEntries }) => remote.map(dayOneEntries, format.number)),
                            null,
                        ),
                    sort: sort.dayOneReturningUsers,
                    defaultDirection: 'desc',
                },
                {
                    name: 'D1RR',
                    alt: 'Day 1 retention rate',
                    type: 'percentage',
                    width: 80,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.flatMap(statistics, ({ dayZeroEntries, dayOneEntries }) =>
                                remote.map2(dayZeroEntries, dayOneEntries, (dayZeroEntries, dayOneEntries) =>
                                    dayZeroEntries ? format.percentage(dayOneEntries / dayZeroEntries) : null,
                                ),
                            ),
                            null,
                        ),
                    sort: sort.dayOneReturningRate,
                    defaultDirection: 'desc',
                },
                {
                    name: 'D1CPX',
                    alt: 'Day 1 cost per conversion',
                    type: 'currency',
                    width: 90,
                    render: ({ statistics }) =>
                        remote.withDefault(
                            remote.flatMap(statistics, ({ dayOneEntries, cost, currency }) =>
                                remote.map(dayOneEntries, (dayOneEntries) =>
                                    dayOneEntries ? format.currency(cost / dayOneEntries, currency) : null,
                                ),
                            ),
                            null,
                        ),
                    sort: sort.dayOneCostPerConversion,
                    defaultDirection: 'desc',
                },
            ]}
        />
    );
};
